/**
 * Dev-only artificial network latency (Session 3 QA aid).
 *
 * Localhost is ~0 ms, which is *exactly why* the missing client-side prediction
 * stayed invisible through the whole jam — all testing was local. To QA the
 * prediction feel before the hosted playtest we inject one-way latency in BOTH
 * directions for the local session:
 *
 *   - Uplink: `room.send` is delayed by `oneWayMs` (so a click takes RTT before
 *     the server even paths — the dead-beat prediction is meant to hide).
 *   - Downlink: the local hero's authoritative sample is read `oneWayMs` in the
 *     past (so the prediction reconciles against stale state, like a real client).
 *
 * Gated to the dev build via the debug dispatcher; never wired in prod.
 */

export interface CubeSample {
  x: number;
  z: number;
  targetX: number;
  targetZ: number;
  moveSpeed: number;
  state: string;
}

type SendFn = (type: string, message?: unknown) => void;

export class LatencySim {
  private oneWayMs = 0;
  private origSend: SendFn | null = null;
  private patchedRoom: { send: SendFn } | null = null;

  /** Ring buffer of (t, sample) for downlink aging of the local hero. */
  private buf: Array<{ t: number; s: CubeSample }> = [];

  get latencyMs(): number { return this.oneWayMs; }
  get enabled(): boolean { return this.oneWayMs > 0; }

  /** Set one-way latency in ms (RTT = 2×). 0 disables. */
  set(ms: number): void {
    this.oneWayMs = Math.max(0, ms | 0);
    if (this.oneWayMs === 0) this.buf.length = 0;
  }

  /**
   * Install the uplink delay by monkey-patching `room.send`. Idempotent; the
   * original is preserved and used when latency is 0.
   */
  attach(room: { send: SendFn }): void {
    if (this.patchedRoom === room && this.origSend) return;
    this.origSend = room.send.bind(room);
    this.patchedRoom = room;
    const self = this;
    room.send = function (type: string, message?: unknown) {
      if (self.oneWayMs > 0) {
        setTimeout(() => self.origSend!(type, message), self.oneWayMs);
      } else {
        self.origSend!(type, message);
      }
    } as SendFn;
  }

  /** Record the live authoritative sample for the local hero each frame. */
  record(s: CubeSample, nowMs: number): void {
    if (this.oneWayMs === 0) return;
    this.buf.push({ t: nowMs, s: { ...s } });
    // Trim anything older than we could possibly need (2× current latency).
    const cutoff = nowMs - this.oneWayMs * 2 - 200;
    while (this.buf.length > 1 && this.buf[0]!.t < cutoff) this.buf.shift();
  }

  /**
   * Return the local hero's sample as seen `oneWayMs` ago. Pass the live sample
   * as `live`; when latency is 0 (or the buffer hasn't filled) the live sample
   * is returned unchanged.
   */
  read(live: CubeSample, nowMs: number): CubeSample {
    if (this.oneWayMs === 0 || this.buf.length === 0) return live;
    const target = nowMs - this.oneWayMs;
    // Find the newest sample at-or-before the target time.
    let chosen: CubeSample | null = null;
    for (let i = this.buf.length - 1; i >= 0; i--) {
      if (this.buf[i]!.t <= target) { chosen = this.buf[i]!.s; break; }
    }
    return chosen ?? this.buf[0]!.s;
  }
}
